import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, PenTool, Upload, Link as LinkIcon, BookOpen } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card } from '../components/ui/card';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CricketBlogs() {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { getAuthHeader, user } = useAuth();
  const [blogs, setBlogs] = useState([]);
  const [showEditor, setShowEditor] = useState(false);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [substackUrl, setSubstackUrl] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchBlogs();
  }, []);

  const fetchBlogs = async () => {
    try {
      const response = await axios.get(`${API}/blogs`);
      setBlogs(response.data);
    } catch (error) {
      console.error('Error fetching blogs:', error);
    }
  };

  const saveBlog = async () => {
    if (!title || !content) {
      toast.error('Please provide title and content');
      return;
    }

    setSaving(true);
    try {
      const wordCount = content.trim().split(/\s+/).length;
      
      await axios.post(`${API}/blogs`, {
        title,
        content,
        substack_url: substackUrl,
        word_count: wordCount
      }, { headers: getAuthHeader() });

      const pointsEarned = wordCount >= 1000 ? 50 : 25;
      toast.success(`Blog published! +${pointsEarned} points earned`);
      
      setShowEditor(false);
      setTitle('');
      setContent('');
      setSubstackUrl('');
      fetchBlogs();
      
      // Refresh user data to show updated points
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (error) {
      toast.error('Failed to publish blog');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ 
      background: theme === 'dark' 
        ? 'linear-gradient(135deg, #0a0e27 0%, #1a1f3a 100%)' 
        : 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)'
    }}>
      <div className={`backdrop-blur-md ${theme === 'dark' ? 'bg-white/5' : 'bg-black/5'} border-b ${theme === 'dark' ? 'border-white/10' : 'border-black/10'}`}>
        <div className="max-w-7xl mx-auto px-4 py-4">
          <button onClick={() => navigate('/')} className={`flex items-center ${theme === 'dark' ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'} mb-4`}>
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Home
          </button>
          <div className="flex items-center justify-between">
            <h1 className={`text-4xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Cricket Blogs</h1>
            <Button onClick={() => setShowEditor(!showEditor)} className="bg-gradient-to-r from-purple-500 to-pink-500">
              <PenTool className="w-4 h-4 mr-2" />
              {showEditor ? 'Cancel' : 'New Blog'}
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {showEditor ? (
          <Card className={`glass-card mb-8 ${theme === 'light' ? 'bg-white/95' : ''}`}>
            <h2 className={`text-2xl font-bold mb-6 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Write New Blog</h2>
            
            <div className="space-y-4">
              <Input
                placeholder="Blog Title..."
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className={theme === 'dark' ? 'bg-white/10 border-white/20 text-white text-xl' : 'bg-white border-gray-300 text-xl'}
              />
              
              <Input
                placeholder="Substack URL (optional)"
                value={substackUrl}
                onChange={(e) => setSubstackUrl(e.target.value)}
                className={theme === 'dark' ? 'bg-white/10 border-white/20 text-white' : 'bg-white border-gray-300'}
              />
              
              <Textarea
                placeholder="Write your cricket blog here... (supports markdown)"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                rows={20}
                className={theme === 'dark' ? 'bg-white/10 border-white/20 text-white' : 'bg-white border-gray-300'}
                style={{ minHeight: '400px', fontFamily: 'monospace' }}
              />
              
              <div className="flex items-center justify-between">
                <span className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                  Word count: {content.replace(/<[^>]*>/g, '').split(' ').filter(w => w).length}
                </span>
                <Button onClick={saveBlog} disabled={saving} className="bg-gradient-to-r from-green-500 to-emerald-600">
                  <Upload className="w-4 h-4 mr-2" />
                  {saving ? 'Publishing...' : 'Publish Blog'}
                </Button>
              </div>
            </div>
          </Card>
        ) : null}

        <div className="space-y-6">
          {blogs.map((blog) => (
            <Card key={blog.id} className={`glass-card ${theme === 'light' ? 'bg-white/95' : ''}`}>
              <div className="space-y-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h2 className={`text-2xl font-bold mb-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{blog.title}</h2>
                    <div className="flex items-center space-x-4 text-sm">
                      <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>
                        {new Date(blog.created_at).toLocaleDateString()}
                      </span>
                      <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>
                        {blog.word_count} words
                      </span>
                      {blog.substack_url && (
                        <a href={blog.substack_url} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:text-blue-400">
                          <LinkIcon className="w-4 h-4 inline" /> Substack
                        </a>
                      )}
                    </div>
                  </div>
                  <BookOpen className="w-8 h-8 text-purple-400" />
                </div>
                <div className={`prose max-w-none ${theme === 'dark' ? 'prose-invert' : ''}`} style={{ whiteSpace: 'pre-wrap' }}>
                  {blog.content.substring(0, 500)}{blog.content.length > 500 ? '...' : ''}
                </div>
                <Button variant="outline" size="sm">Read More</Button>
              </div>
            </Card>
          ))}
          
          {blogs.length === 0 && !showEditor && (
            <div className="text-center py-16">
              <p className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} mb-4`}>No blogs yet</p>
              <Button onClick={() => setShowEditor(true)}>Write First Blog</Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
